"use client";

import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { PAY_AT_ONCE, SPREAD_THE_COST } from "@/constants";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import { AutoPaySectionSchemaType } from "@/schema/renewal-page/payment-summary-page.schema";
import { RootState } from "@/store/root-reducer";

import AutoPaySection from "./auto-pay-section";

type AutoPaySectionContent = {
  content: AutoPaySectionSchemaType;
};

type AutoPaySectionContentData = {
  title: string;
  description: string;
  ctaOne: {
    ctaOneTitle: string;
    ctaOneDescription: string;
  };
  ctaTwo: {
    ctaTwoTitle: string;
    ctaTwoDescription: string;
  };
  directDebitText: string;
};

export default function AutoPaySectionWrapper({
  content,
}: Readonly<AutoPaySectionContent>) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { type } = useSelector((state: RootState) => state.paymentConsents);
  const { setAutoPaySelected } = useRenewalPaymentSummaryStateContext();
  const { tripDetails, paymentDetails, mtaMedicalConsent } = attributes;
  const paymentOption = paymentDetails?.paymentOption;
  const isAutoPay =
    paymentDetails?.paymentOptions[0]?.cardPayment?.autoPayPayment
      ?.isAutoPayEnabled;
  const isAutoRenewal = !paymentDetails?.optOutAutoRenewal;

  const lastFourDigits =
    paymentDetails?.paymentOptions[0]?.cardPayment?.autoPayPayment
      ?.lastFourDigit;
  const policyEndDate = tripDetails?.endDate;
  const depositAmount =
    paymentDetails?.paymentOptions[0]?.cardPayment?.premiumCreditPayment[0]?.pcl
      ?.depositAmount;
  const mtaPrice =
    paymentDetails?.paymentOptions[0]?.cardPayment?.card?.mtaPrice;
  const isMTA =
    mtaMedicalConsent?.isTravellingBeforeRenewal &&
    mtaPrice !== "0.00" &&
    mtaPrice !== undefined;
  const totalPrice =
    paymentDetails?.paymentOptions[0]?.cardPayment?.card?.totalPrice;
  const renewalPrice =
    paymentDetails?.paymentOptions[0]?.cardPayment?.card?.price;

  const isVoucherImpact = useMemo(
    () =>
      paymentDetails?.discounts?.voucher &&
      paymentDetails?.discounts?.voucher.length > 0 &&
      type === "PAY_AT_ONCE",
    [paymentDetails, type]
  );

  const renderContent = () => {
    let idPrefix;
    if (type === SPREAD_THE_COST) {
      idPrefix = "M";
    } else if (type === PAY_AT_ONCE) {
      idPrefix = "F";
    } else if (paymentOption === SPREAD_THE_COST) {
      idPrefix = "M";
    } else if (paymentOption === PAY_AT_ONCE) {
      idPrefix = "F";
    } else idPrefix = "F";

    let autoPayStatus = isAutoPay ? "ON" : "OFF";
    const autoRenewalStatus = isAutoRenewal ? "ON" : "OFF";
    const mtaStatus = isMTA ? "ON" : "OFF";

    if (isVoucherImpact) {
      autoPayStatus = "OFF";
    }

    const id = `${idPrefix}-${autoPayStatus}-${autoRenewalStatus}-${mtaStatus}`;
    return Object.values(content).find((item) => item.id === id);
  };

  const data = renderContent();

  const replacePlaceholders = (sectionData: AutoPaySectionContentData) => {
    const { title, description, ctaOne } = sectionData;

    return {
      ...sectionData,
      title: title
        ? title
            .replace("<DEPOSIT_AMOUNT>", depositAmount ?? "")
            .replace(
              "<MTA_DEPOSIT_AMOUNT>",
              String(
                (
                  (Number(mtaPrice) || 0) + (Number(depositAmount) || 0)
                ).toFixed(2)
              )
            )
        : "",
      description: description
        ? description
            .replace("&lt;MTA_PRICE&gt;", mtaPrice ?? "")
            .replace("&lt;TOTAL_PRICE&gt;", totalPrice ?? "")
            .replace("&lt;DEPOSIT_AMOUNT&gt;", depositAmount ?? "")
            .replace("&lt;RENEWAL_PRICE&gt;", renewalPrice ?? "")
        : "",

      ctaOne: ctaOne
        ? {
            ctaOneTitle: ctaOne.ctaOneTitle
              ? ctaOne.ctaOneTitle.replace(
                  "<LAST_FOUR_DIGIT>",
                  lastFourDigits ?? ""
                )
              : "",
            ctaOneDescription: ctaOne.ctaOneDescription
              ? ctaOne.ctaOneDescription.replace(
                  "<POLICY_END_DATE>",
                  `${dayjs(policyEndDate).format("DD/MM/YYYY").toString()}`
                )
              : "",
          }
        : { ctaOneTitle: "", ctaOneDescription: "" },
    };
  };
  const { title, description, directDebitText, ctaOne, ctaTwo } =
    replacePlaceholders(
      data ?? {
        title: "",
        directDebitText: "",
        description: "",
        ctaOne: { ctaOneTitle: "", ctaOneDescription: "" },
        ctaTwo: { ctaTwoTitle: "", ctaTwoDescription: "" },
      }
    );

  // Return an empty fragment if all values are empty
  if (
    !title &&
    !description &&
    !directDebitText &&
    !ctaOne?.ctaOneTitle &&
    !ctaOne?.ctaOneDescription &&
    !ctaTwo?.ctaTwoTitle &&
    !ctaTwo?.ctaTwoDescription
  ) {
    if (isVoucherImpact) {
      setAutoPaySelected(false);
    }

    return <></>;
  }

  return (
    <AutoPaySection
      title={title}
      description={description}
      directDebitText={directDebitText}
      ctaOne={ctaOne}
      ctaTwo={ctaTwo}
      isVoucherImpact={isVoucherImpact as boolean}
    />
  );
}
